import React from "react"
import {
  AttachPosition,
  StepType,
  TutorialConfig,
  TutorialStep,
} from "../types/tutorial-config"
import SelectorPicker from "./selector-picker"
import StepActionEditor from "./step-action-editor"
import VariableTextInput from "./variable-text-input"
import { ValidationIssue } from "./validation"

type Props = {
  step: TutorialStep
  config: TutorialConfig
  issues: ValidationIssue[]
  onChange: (step: TutorialStep) => void
}

const POSITIONS: AttachPosition[] = ["top", "bottom", "left", "right"]
const STEP_TYPES: StepType[] = [
  "standard",
  "click-advance",
  "search-wait",
  "tree-select",
  "drag-drop",
  "modal-wait",
  "final",
]

const StepEditor: React.FC<Props> = ({ step, config, issues, onChange }) => {
  const treeMappingNames = config.treeNodeMappings.map((m) => m.name)
  const variableNames = [
    ...Object.keys(config.variables ?? {}),
    ...treeMappingNames,
  ]
  const stepIssues = issues.filter(
    (i) => i.scope.kind === "step" && i.scope.stepId === step.id,
  )
  const fieldIssues = (field: string) =>
    stepIssues.filter(
      (i) => i.scope.kind === "step" && i.scope.field === field,
    )

  const update = (patch: Partial<TutorialStep>) => onChange({ ...step, ...patch })

  return (
    <div className="p-3 space-y-3 text-sm">
      {stepIssues.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded p-2">
          <div className="text-xs font-semibold text-red-700 mb-1">Issues</div>
          {stepIssues.map((i, idx) => (
            <div key={idx} className={i.level === "error" ? "text-red-700" : "text-amber-700"}>
              • {i.message}
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col">
          <span className="text-xs text-gray-600">Step ID</span>
          <input
            type="text"
            value={step.id}
            onChange={(e) => update({ id: e.target.value })}
            className="border rounded px-2 py-1 font-mono"
          />
          {fieldIssues("id").length > 0 && (
            <span className="text-xs text-red-600">
              {fieldIssues("id")[0].message}
            </span>
          )}
        </label>
        <label className="flex flex-col">
          <span className="text-xs text-gray-600">Type</span>
          <select
            value={step.type}
            onChange={(e) => update({ type: e.target.value as StepType })}
            className="border rounded px-2 py-1">
            {STEP_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="flex flex-col">
        <span className="text-xs text-gray-600">Text (HTML supported)</span>
        <VariableTextInput
          value={step.text}
          onChange={(v) => update({ text: v })}
          variableNames={variableNames}
          asArray={Array.isArray(step.text)}
          rows={4}
        />
        <button
          type="button"
          onClick={() =>
            update({
              text: Array.isArray(step.text)
                ? (step.text.join("\n\n") as string)
                : ([step.text || ""] as string[]),
            })
          }
          className="text-xs text-indigo-600 mt-1 self-start">
          {Array.isArray(step.text)
            ? "Switch to single string"
            : "Switch to array of strings"}
        </button>
      </label>

      {step.type !== "final" && (
        <fieldset className="border rounded p-2">
          <legend className="text-xs text-gray-600 px-1">Attach to</legend>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <span className="text-xs text-gray-500">Element</span>
              <SelectorPicker
                value={step.attachTo?.element ?? ""}
                onChange={(v) =>
                  update({
                    attachTo: {
                      element: v,
                      on: step.attachTo?.on ?? "bottom",
                    },
                  })
                }
                treeNodeMappingNames={treeMappingNames}
              />
            </div>
            <label className="flex flex-col">
              <span className="text-xs text-gray-500">Side</span>
              <select
                value={step.attachTo?.on ?? "bottom"}
                onChange={(e) =>
                  update({
                    attachTo: {
                      element: step.attachTo?.element ?? "",
                      on: e.target.value as AttachPosition,
                    },
                  })
                }
                className="border rounded px-1 py-1">
                {POSITIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </fieldset>
      )}

      {(step.type === "click-advance" ||
        step.type === "search-wait" ||
        step.type === "tree-select" ||
        step.type === "modal-wait") && (
        <fieldset className="border rounded p-2">
          <legend className="text-xs text-gray-600 px-1">Advance on</legend>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <span className="text-xs text-gray-500">Selector</span>
              <SelectorPicker
                value={step.advanceOn?.selector ?? ""}
                onChange={(v) =>
                  update({
                    advanceOn: {
                      selector: v,
                      event: step.advanceOn?.event ?? "click",
                    },
                  })
                }
                treeNodeMappingNames={treeMappingNames}
              />
            </div>
            <label className="flex flex-col">
              <span className="text-xs text-gray-500">Event</span>
              <input
                type="text"
                value={step.advanceOn?.event ?? ""}
                onChange={(e) =>
                  update({
                    advanceOn: {
                      selector: step.advanceOn?.selector ?? "",
                      event: e.target.value,
                    },
                  })
                }
                placeholder="click, customSearchEvent, ..."
                className="border rounded px-1 py-1 font-mono"
              />
            </label>
          </div>
        </fieldset>
      )}

      {step.type === "modal-wait" && (
        <label className="flex flex-col">
          <span className="text-xs text-gray-600">Wait for element</span>
          <SelectorPicker
            value={step.waitForElement ?? ""}
            onChange={(v) => update({ waitForElement: v })}
            treeNodeMappingNames={treeMappingNames}
          />
        </label>
      )}

      <StepActionEditor
        label="onShow actions"
        actions={step.onShow}
        treeMappingNames={treeMappingNames}
        onChange={(actions) =>
          update({ onShow: actions.length > 0 ? actions : undefined })
        }
      />
      <StepActionEditor
        label="onHide actions"
        actions={step.onHide}
        treeMappingNames={treeMappingNames}
        onChange={(actions) =>
          update({ onHide: actions.length > 0 ? actions : undefined })
        }
      />

      <details className="border rounded p-2">
        <summary className="cursor-pointer text-xs text-gray-600">
          Advanced
        </summary>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <label className="flex flex-col">
            <span className="text-xs text-gray-600">Classes</span>
            <input
              type="text"
              value={step.classes ?? ""}
              onChange={(e) => update({ classes: e.target.value || undefined })}
              className="border rounded px-2 py-1 font-mono"
            />
          </label>
          <label className="flex flex-col">
            <span className="text-xs text-gray-600">Modal padding</span>
            <input
              type="number"
              value={step.modalOverlayOpeningPadding ?? ""}
              onChange={(e) =>
                update({
                  modalOverlayOpeningPadding:
                    e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
              className="border rounded px-2 py-1 font-mono"
            />
          </label>
          <label className="flex flex-col">
            <span className="text-xs text-gray-600">Modal radius</span>
            <input
              type="number"
              value={step.modalOverlayOpeningRadius ?? ""}
              onChange={(e) =>
                update({
                  modalOverlayOpeningRadius:
                    e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
              className="border rounded px-2 py-1 font-mono"
            />
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={step.arrow !== false}
              onChange={(e) => update({ arrow: e.target.checked })}
            />
            <span className="text-xs text-gray-600">Show arrow</span>
          </label>
        </div>
      </details>
    </div>
  )
}

export default StepEditor
